<template>
  <div class="card policy-chart-card h-100">
    <div class="card-header pb-0">
      <div class="d-flex align-items-center justify-content-between">
        <h6 class="mb-0">{{ title }}</h6>
        <span
          v-if="count !== '' && count !== null && count !== undefined"
          class="policy-chart-metric mb-0"
        >{{ count }}</span>
      </div>
    </div>
    <div class="card-body policy-chart-body">
      <div v-if="hasData" class="custom-line-chart-wrap" @mouseleave="hoveredIndex = -1">
        <svg
          class="custom-line-svg"
          viewBox="0 0 500 156"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient :id="uid + '-line-grad'" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#3699FF" stop-opacity="0.38" />
              <stop offset="75%" stop-color="#3699FF" stop-opacity="0.08" />
              <stop offset="100%" stop-color="#3699FF" stop-opacity="0.0" />
            </linearGradient>
            <filter :id="uid + '-glow'" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#3699FF" flood-opacity="0.4" />
            </filter>
          </defs>

          <!-- Horizontal Grid Lines -->
          <g class="chart-grid">
            <line
              v-for="grid in gridLines"
              :key="'grid-' + grid.y"
              x1="30"
              :y1="grid.y"
              x2="485"
              :y2="grid.y"
              class="grid-line"
            />
          </g>

          <!-- Y Axis Labels -->
          <g class="chart-y-labels">
            <text
              v-for="grid in gridLines"
              :key="'ylabel-' + grid.y"
              x="22"
              :y="grid.y + 3.5"
              text-anchor="end"
              class="axis-tick-text"
            >{{ grid.value }}</text>
          </g>

          <!-- Area Gradient Fill -->
          <path
            v-if="areaPath"
            :d="areaPath"
            :fill="'url(#' + uid + '-line-grad)'"
          />

          <!-- Line Stroke -->
          <path
            v-if="linePath"
            :d="linePath"
            fill="none"
            stroke="#3699FF"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            :filter="'url(#' + uid + '-glow)'"
          />

          <!-- Interactive Points & Hover Columns -->
          <g class="chart-points">
            <g
              v-for="(pt, idx) in points"
              :key="'pt-' + idx"
              @mouseenter="hoveredIndex = idx"
            >
              <!-- Transparent wide hover zone -->
              <rect
                :x="pt.x - 18"
                y="10"
                width="36"
                height="115"
                fill="transparent"
                class="hover-zone"
              />

              <!-- Active hover vertical line -->
              <line
                v-if="hoveredIndex === idx"
                :x1="pt.x"
                y1="15"
                :x2="pt.x"
                y2="125"
                class="hover-vline"
              />

              <!-- Outer point circle -->
              <circle
                :cx="pt.x"
                :cy="pt.y"
                :r="hoveredIndex === idx ? 5.5 : 3.5"
                fill="#3699FF"
                :stroke-width="hoveredIndex === idx ? 2.5 : 1.5"
                class="point-circle"
                :class="{ 'is-active': hoveredIndex === idx }"
              />
            </g>
          </g>

          <!-- X Axis Labels -->
          <g class="chart-x-labels">
            <text
              v-for="(pt, idx) in points"
              :key="'xlabel-' + idx"
              :x="pt.x"
              y="144"
              text-anchor="middle"
              class="axis-tick-text"
              :class="{ 'is-active': hoveredIndex === idx }"
            >{{ pt.label }}</text>
          </g>
        </svg>

        <!-- Floating Tooltip -->
        <div
          v-if="hoveredPoint"
          class="custom-chart-tooltip"
          :style="tooltipStyle"
        >
          <div class="tooltip-label">{{ hoveredPoint.label }}</div>
          <div class="tooltip-value">{{ title }}: <strong>{{ hoveredPoint.value }}</strong></div>
        </div>
      </div>

      <chart-empty-state
        v-else
        height="156px"
        icon="fas fa-chart-line"
      />
    </div>
  </div>
</template>

<script>
import ChartEmptyState from "@/project/Global/Components/ChartEmptyState.vue";

export default {
  name: "ProgressLineChart",
  components: { ChartEmptyState },
  props: {
    title: { type: String, default: "Göndərişlər" },
    count: { type: [Number, String], default: 0 },
    chart: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      uid: `line-${Math.random().toString(36).slice(2, 8)}`,
      hoveredIndex: -1,
    };
  },
  computed: {
    rawLabels() {
      return this.chart?.labels || [];
    },
    rawData() {
      return (this.chart?.data || []).map((v) => Number(v) || 0);
    },
    hasData() {
      if (!this.rawData.length) {
        return false;
      }
      return this.rawData.some((value) => value > 0);
    },
    maxValue() {
      const max = Math.max(...this.rawData, 0);
      if (max <= 0) return 5;
      if (max <= 5) return 5;
      if (max <= 10) return 10;
      if (max <= 20) return 20;
      return Math.ceil(max * 1.2);
    },
    gridLines() {
      const topY = 20;
      const bottomY = 125;
      const max = this.maxValue;
      return [
        { y: topY, value: max },
        { y: Math.round((topY + bottomY) / 2), value: Math.round(max / 2) },
        { y: bottomY, value: 0 },
      ];
    },
    points() {
      const labels = this.rawLabels;
      const data = this.rawData;
      const count = Math.max(labels.length, data.length);
      if (count === 0) return [];

      const leftX = 45;
      const rightX = 475;
      const topY = 20;
      const bottomY = 125;
      const rangeY = bottomY - topY;
      const max = this.maxValue;

      const stepX = count > 1 ? (rightX - leftX) / (count - 1) : 0;

      return data.map((val, i) => {
        const x = count === 1 ? (leftX + rightX) / 2 : leftX + i * stepX;
        const normalized = max > 0 ? val / max : 0;
        const y = bottomY - normalized * rangeY;
        return {
          x: Math.round(x * 10) / 10,
          y: Math.round(y * 10) / 10,
          value: val,
          label: labels[i] || `${i + 1}`,
        };
      });
    },
    linePath() {
      const pts = this.points;
      if (!pts.length) return "";
      if (pts.length === 1) {
        return `M ${pts[0].x - 10} ${pts[0].y} L ${pts[0].x + 10} ${pts[0].y}`;
      }

      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i === 0 ? 0 : i - 1];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const cp1x = p1.x + (p2.x - p0.x) / 5.5;
        const cp1y = p1.y + (p2.y - p0.y) / 5.5;
        const cp2x = p2.x - (p3.x - p1.x) / 5.5;
        const cp2y = p2.y - (p3.y - p1.y) / 5.5;
        d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
      }
      return d;
    },
    areaPath() {
      const pts = this.points;
      if (!pts.length || !this.linePath) return "";
      const bottomY = 125;
      const last = pts[pts.length - 1];
      const first = pts[0];
      return `${this.linePath} L ${last.x} ${bottomY} L ${first.x} ${bottomY} Z`;
    },
    hoveredPoint() {
      if (this.hoveredIndex >= 0 && this.hoveredIndex < this.points.length) {
        return this.points[this.hoveredIndex];
      }
      return null;
    },
    tooltipStyle() {
      if (!this.hoveredPoint) return {};
      const pctX = (this.hoveredPoint.x / 500) * 100;
      const pctY = (this.hoveredPoint.y / 156) * 100;
      return {
        left: `${pctX}%`,
        top: `${Math.max(10, pctY - 18)}%`,
        transform: "translate(-50%, -100%)",
      };
    },
  },
};
</script>

<style scoped>
.custom-line-chart-wrap {
  position: relative;
  width: 100%;
  height: 156px;
  user-select: none;
}

.custom-line-svg {
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
}

.grid-line {
  stroke: var(--app-border, rgba(120, 130, 145, 0.2));
  stroke-dasharray: 4 4;
  stroke-width: 1;
}

.axis-tick-text {
  font-size: 10px;
  fill: var(--app-text, #64748b);
  font-family: inherit;
  font-weight: 500;
  transition: fill 0.2s ease;
}

.axis-tick-text.is-active {
  fill: var(--app-heading, #111827);
  font-weight: 700;
}

.hover-zone {
  cursor: pointer;
}

.hover-vline {
  stroke: rgba(54, 153, 255, 0.45);
  stroke-width: 1.5;
  stroke-dasharray: 2 2;
}

.point-circle {
  transition: r 0.2s ease, stroke-width 0.2s ease;
  cursor: pointer;
  stroke: var(--app-surface, #ffffff);
}

.custom-chart-tooltip {
  position: absolute;
  pointer-events: none;
  background: var(--app-surface-elevated, #ffffff);
  border: 1px solid var(--app-border, #e2e8f0);
  border-radius: 6px;
  padding: 5px 9px;
  font-size: 11px;
  color: var(--app-heading, #111827);
  white-space: nowrap;
  box-shadow: var(--app-shadow-md, 0 4px 14px rgba(0, 0, 0, 0.15));
  z-index: 10;
  transition: left 0.15s ease, top 0.15s ease;
}

.tooltip-label {
  font-size: 10px;
  color: var(--app-text, #64748b);
  margin-bottom: 2px;
}

.tooltip-value {
  font-weight: 600;
  color: #3699FF;
}
</style>
